import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';

export type DeliveryTimeSlotRow = {
  startTime: string;
  endTime: string;
  maxPoints: string;
};

export const MAX_DELIVERY_TIME_SLOTS = 8;

const TIME_OPTIONS = [
  '9 AM',
  '10 AM',
  '11 AM',
  '12 PM',
  '1 PM',
  '2 PM',
  '3 PM',
  '4 PM',
  '5 PM',
  '6 PM',
  '7 PM',
  '8 PM',
  '9 PM',
  '10 PM',
  '11 PM',
];

/** Delivery window is 9 AM–11 PM; bare hours like "10" / "2" map into that range. */
const inferMeridiem = (hour: number): 'AM' | 'PM' => {
  if (hour >= 9 && hour <= 11) return 'AM';
  return 'PM';
};

const normalizeTimeToken = (value: string): string => {
  const trimmed = value.trim().replace(/\s+/g, ' ');
  if (!trimmed) return '';

  // Already a listed option
  const listed = TIME_OPTIONS.find(
    (option) => option.toLowerCase() === trimmed.toLowerCase()
  );
  if (listed) return listed;

  const match = trimmed.match(
    /^(1[0-2]|0?[1-9])(?::([0-5][0-9]))?\s*(am|pm)?$/i
  );
  if (!match) return trimmed;

  const hourNum = Number(match[1]);
  const hour = String(hourNum);
  const meridiem = (match[3] || inferMeridiem(hourNum)).toUpperCase() as
    | 'AM'
    | 'PM';
  const label = `${hour} ${meridiem}`;
  return label;
};

const ensureTimeOption = (value: string, options: string[]): string[] => {
  const normalized = normalizeTimeToken(value);
  if (!normalized || options.includes(normalized)) return options;
  // Only append unknowns that look like times — never bare digits like "10"
  if (/^\d+$/.test(normalized)) return options;
  return [...options, normalized];
};

const parseSlotLabel = (
  slot: string
): { startTime: string; endTime: string } => {
  const parts = String(slot || '')
    .split(/\s+to\s+/i)
    .map((part) => part.trim());

  if (parts.length < 2) {
    return { startTime: '', endTime: '' };
  }

  return {
    startTime: normalizeTimeToken(parts[0]),
    endTime: normalizeTimeToken(parts[1]),
  };
};

export const parseDeliveryTimeSlotsValue = (
  value: string
): DeliveryTimeSlotRow[] => {
  if (!value?.trim()) {
    return [{ startTime: '', endTime: '', maxPoints: '' }];
  }

  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed) || parsed.length === 0) {
      return [{ startTime: '', endTime: '', maxPoints: '' }];
    }

    return parsed.map((item) => {
      if (typeof item === 'string') {
        const { startTime, endTime } = parseSlotLabel(item);
        return { startTime, endTime, maxPoints: '100' };
      }

      const { startTime, endTime } = parseSlotLabel(String(item?.slot || ''));
      return {
        startTime,
        endTime,
        maxPoints:
          item?.maxPoints != null && item?.maxPoints !== ''
            ? String(item.maxPoints).replace(/\D/g, '')
            : '',
      };
    });
  } catch {
    return [{ startTime: '', endTime: '', maxPoints: '' }];
  }
};

export const stringifyDeliveryTimeSlots = (
  rows: DeliveryTimeSlotRow[]
): string => {
  const slots = rows
    .map((row) => {
      const startTime = normalizeTimeToken(row.startTime.trim());
      const endTime = normalizeTimeToken(row.endTime.trim());
      const maxPoints = Number(row.maxPoints);

      if (!startTime || !endTime || !maxPoints || maxPoints <= 0) {
        return null;
      }

      return {
        slot: `${startTime} to ${endTime}`,
        maxPoints,
      };
    })
    .filter(Boolean);

  return JSON.stringify(slots);
};

const clockMinutes = (
  hour: string,
  minute: string | undefined,
  period: string
): number | null => {
  let hours = parseInt(hour, 10);
  const minutes = parseInt(minute || '0', 10);
  const marker = period.toLowerCase();
  if (marker === 'pm' && hours !== 12) {
    hours += 12;
  } else if (marker === 'am' && hours === 12) {
    hours = 0;
  }
  if (
    hours > 23 ||
    minutes > 59 ||
    Number.isNaN(hours) ||
    Number.isNaN(minutes)
  ) {
    return null;
  }
  return hours * 60 + minutes;
};

/** True when the label uses lowercase am/pm (AM/PM uppercase is required). */
export const hasLowercaseMeridiem = (slot?: string | null): boolean =>
  /\b(am|pm)\b/.test(String(slot || ''));

/** "11 AM to 3 PM" → start/end minutes (parse accepts either case; save must be AM/PM). */
export const slotRangeMinutes = (
  label: string
): { start: number; end: number } | null => {
  const match = String(label || '')
    .trim()
    .match(
      /^(\d{1,2})(?::(\d{2}))?\s*(am|pm)\s*(?:to|–|—|-)\s*(\d{1,2})(?::(\d{2}))?\s*(am|pm)\s*$/i
    );
  if (!match) return null;

  const start = clockMinutes(match[1], match[2], match[3]);
  let end = clockMinutes(match[4], match[5], match[6]);
  if (start == null || end == null) return null;
  if (end <= start) {
    end += 24 * 60;
  }
  return { start, end };
};

/** Half-open: "11 AM to 2 PM" + "2 PM to 6 PM" is OK; "11 AM to 3 PM" + "2 PM to 6 PM" is not. */
export const findOverlappingDeliverySlots = (
  slots: Array<{ slot?: string | null } | string>
): { a: string; b: string } | null => {
  const ranges: Array<{ slot: string; range: { start: number; end: number } }> =
    [];

  for (const item of slots || []) {
    const slot = typeof item === 'string' ? item : String(item?.slot || '');
    const range = slotRangeMinutes(slot);
    if (!range) continue;
    ranges.push({ slot: slot.trim(), range });
  }

  for (let i = 0; i < ranges.length; i++) {
    for (let j = i + 1; j < ranges.length; j++) {
      const a = ranges[i].range;
      const b = ranges[j].range;
      if (a.start < b.end && b.start < a.end) {
        return { a: ranges[i].slot, b: ranges[j].slot };
      }
    }
  }
  return null;
};

export const getDeliverySlotsOverlapMessage = (
  rows: DeliveryTimeSlotRow[]
): string | null => {
  const slots = rows
    .map((row) => {
      const startTime = normalizeTimeToken(row.startTime.trim());
      const endTime = normalizeTimeToken(row.endTime.trim());
      if (!startTime || !endTime) return null;
      return { slot: `${startTime} to ${endTime}` };
    })
    .filter(Boolean) as Array<{ slot: string }>;

  for (const item of slots) {
    if (hasLowercaseMeridiem(item.slot)) {
      return `Use AM/PM in uppercase (e.g. "2 PM to 6 PM"), not am/pm. Invalid: "${item.slot}"`;
    }
  }

  const overlap = findOverlappingDeliverySlots(slots);
  if (!overlap) return null;
  return `Delivery slots overlap: "${overlap.a}" and "${overlap.b}". Adjust the times so they do not overlap.`;
};

interface Props {
  value: string;
  onChange: (jsonValue: string) => void;
  error?: boolean;
  helperText?: React.ReactNode;
  showTitle?: boolean;
  allowEmpty?: boolean;
  onRemoveLast?: () => void;
}

const headerLabelSx = {
  color: 'text.secondary',
  fontSize: 13,
  fontWeight: 400,
};

const selectFieldSx = {
  minWidth: 0,
  width: '100%',
  '& .MuiSelect-select': {
    pr: '32px !important',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& .MuiSelect-icon': {
    right: 6,
  },
};

const DeliveryTimeSlotsEditor = ({
  value,
  onChange,
  error,
  helperText,
  showTitle = true,
  allowEmpty = false,
  onRemoveLast,
}: Props) => {
  const [rows, setRows] = useState<DeliveryTimeSlotRow[]>(() =>
    parseDeliveryTimeSlotsValue(value)
  );

  useEffect(() => {
    setRows(parseDeliveryTimeSlotsValue(value));
  }, [value]);

  const commitRows = (nextRows: DeliveryTimeSlotRow[]) => {
    setRows(nextRows);
    onChange(stringifyDeliveryTimeSlots(nextRows));
  };

  const handleRowChange = (
    index: number,
    field: keyof DeliveryTimeSlotRow,
    fieldValue: string
  ) => {
    const nextValue =
      field === 'maxPoints'
        ? // Digits only — block dash, decimal, and other non-numeric input
          fieldValue.replace(/\D/g, '')
        : fieldValue;

    commitRows(
      rows.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: nextValue } : row
      )
    );
  };

  const handleAddRow = () => {
    if (rows.length >= MAX_DELIVERY_TIME_SLOTS) return;
    commitRows([...rows, { startTime: '', endTime: '', maxPoints: '' }]);
  };

  const canAddMore = rows.length < MAX_DELIVERY_TIME_SLOTS;
  const overlapMessage = getDeliverySlotsOverlapMessage(rows);

  const handleRemoveRow = (index: number) => {
    if (rows.length === 1) {
      if (allowEmpty) {
        setRows([]);
        onChange('[]');
        onRemoveLast?.();
        return;
      }
      commitRows([{ startTime: '', endTime: '', maxPoints: '' }]);
      return;
    }
    commitRows(rows.filter((_, rowIndex) => rowIndex !== index));
  };

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      {showTitle ? (
        <Typography
          variant='h6'
          sx={{ mb: 2, fontWeight: 600 }}
        >
          Time slots
        </Typography>
      ) : null}

      {rows.length > 0 && (
      <Stack
        direction='row'
        spacing={1.5}
        alignItems='center'
        sx={{
          px: 0.5,
          mb: 1,
          display: { xs: 'none', sm: 'flex' },
        }}
      >
        <Typography sx={{ flex: 1, minWidth: 0, ...headerLabelSx }}>
          Start
        </Typography>
        <Typography sx={{ flex: 1, minWidth: 0, ...headerLabelSx }}>
          End
        </Typography>
        <Typography sx={{ flex: 1, minWidth: 0, ...headerLabelSx }}>
          Total points
        </Typography>
        <Box sx={{ width: 40, flexShrink: 0 }} />
      </Stack>
      )}

      <Stack spacing={{ xs: 1.5, sm: 0 }}>
        {rows.map((row, index) => {
          const startTime = normalizeTimeToken(row.startTime);
          const endTime = normalizeTimeToken(row.endTime);
          const startOptions = ensureTimeOption(startTime, TIME_OPTIONS);
          const endOptions = ensureTimeOption(endTime, TIME_OPTIONS);

          return (
            <Box
              key={`delivery-slot-${index}`}
              sx={{
                py: { xs: 1.5, sm: 1 },
                px: { xs: 1.5, sm: 0 },
                border: { xs: '1px solid', sm: 'none' },
                borderColor: { xs: 'divider', sm: 'transparent' },
                borderRadius: { xs: 1.5, sm: 0 },
                bgcolor: { xs: 'grey.50', sm: 'transparent' },
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 1.25, sm: 1.5 }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
              >
                <Stack
                  direction='row'
                  spacing={1.25}
                  sx={{ flex: { sm: 2 }, minWidth: 0, width: '100%' }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        ...headerLabelSx,
                        mb: 0.5,
                        display: { xs: 'block', sm: 'none' },
                      }}
                    >
                      Start
                    </Typography>
                    <FormControl
                      size='small'
                      fullWidth
                      sx={selectFieldSx}
                    >
                      <Select
                        displayEmpty
                        value={startTime}
                        onChange={(e) =>
                          handleRowChange(index, 'startTime', e.target.value)
                        }
                        renderValue={(selected) =>
                          selected ? (
                            <Typography
                              fontWeight={400}
                              noWrap
                            >
                              {selected}
                            </Typography>
                          ) : (
                            <Typography color='text.disabled'>Start</Typography>
                          )
                        }
                      >
                        {startOptions.map((time) => (
                          <MenuItem
                            key={`start-${time}`}
                            value={time}
                          >
                            {time}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        ...headerLabelSx,
                        mb: 0.5,
                        display: { xs: 'block', sm: 'none' },
                      }}
                    >
                      End
                    </Typography>
                    <FormControl
                      size='small'
                      fullWidth
                      sx={selectFieldSx}
                    >
                      <Select
                        displayEmpty
                        value={endTime}
                        onChange={(e) =>
                          handleRowChange(index, 'endTime', e.target.value)
                        }
                        renderValue={(selected) =>
                          selected ? (
                            <Typography
                              fontWeight={400}
                              noWrap
                            >
                              {selected}
                            </Typography>
                          ) : (
                            <Typography color='text.disabled'>End</Typography>
                          )
                        }
                      >
                        {endOptions.map((time) => (
                          <MenuItem
                            key={`end-${time}`}
                            value={time}
                          >
                            {time}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Stack>

                <Stack
                  direction='row'
                  spacing={1}
                  alignItems='flex-end'
                  sx={{ flex: { sm: 1 }, minWidth: 0, width: '100%' }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        ...headerLabelSx,
                        mb: 0.5,
                        display: { xs: 'block', sm: 'none' },
                      }}
                    >
                      Total points
                    </Typography>
                    <TextField
                      size='small'
                      type='text'
                      fullWidth
                      value={row.maxPoints}
                      placeholder='0'
                      onChange={(e) =>
                        handleRowChange(index, 'maxPoints', e.target.value)
                      }
                      autoComplete='off'
                      inputProps={{
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        autoComplete: 'off',
                        'data-form-type': 'other',
                        'data-lpignore': 'true',
                        'data-1p-ignore': 'true',
                      }}
                      sx={{
                        minWidth: 0,
                        '& .MuiInputBase-input': {
                          fontWeight: 400,
                          py: 1,
                        },
                      }}
                    />
                  </Box>

                  <IconButton
                    aria-label='Remove slot'
                    size='small'
                    onClick={() => handleRemoveRow(index)}
                    sx={{
                      width: 40,
                      height: 40,
                      flexShrink: 0,
                      color: 'text.primary',
                    }}
                  >
                    <CloseIcon fontSize='small' />
                  </IconButton>
                </Stack>
              </Stack>
            </Box>
          );
        })}
      </Stack>

      {canAddMore ? (
        <Button
          fullWidth
          startIcon={<AddIcon />}
          onClick={handleAddRow}
          sx={{
            mt: 2,
            py: 1.25,
            textTransform: 'none',
            fontWeight: 500,
            color: 'primary.main',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            '&:hover': {
              borderStyle: 'dashed',
              borderColor: 'primary.main',
              bgcolor: 'action.hover',
            },
          }}
        >
          Add time slot
        </Button>
      ) : (
        <Typography
          variant='body2'
          color='text.secondary'
          sx={{ mt: 2, textAlign: 'center' }}
        >
          Maximum {MAX_DELIVERY_TIME_SLOTS} time slots allowed
        </Typography>
      )}

      {overlapMessage ? (
        <FormHelperText error sx={{ mt: 1.5 }}>
          {overlapMessage}
        </FormHelperText>
      ) : null}

      {error && helperText ? (
        <FormHelperText error>{helperText}</FormHelperText>
      ) : null}
    </Box>
  );
};

export default DeliveryTimeSlotsEditor;
